import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Search, Package, MapPin, Save, RotateCcw,
  CheckCircle, AlertCircle, Calendar, User, FileText,
  TrendingUp, DollarSign, HelpCircle
} from 'lucide-react';
import apiClient from '../services/api';
import PODPrintModal from './PODPrintModal';

/* ─── Debounce ── */
function useDebounce(value, delay) {
  const [d, setD] = useState(value);
  useEffect(() => { const t=setTimeout(()=>setD(value),delay); return()=>clearTimeout(t); },[value,delay]);
  return d;
}
const getSessionData = () => {
  try { const s=localStorage.getItem('ltms_session'); if(!s) return null; const p=JSON.parse(s); return p?.session||p; } catch { return null; }
};
const companyName = getSessionData()?.companyname || '';

/* ─── TypeBadge ── */
const TypeBadge = ({ type }) => {
  const map = { LT:{bg:'#ddd6fe',color:'#5b21b6',label:'Land Transport'}, D:{bg:'#d1fae5',color:'#065f46',label:'Domestic'}, I:{bg:'#fef3c7',color:'#92400e',label:'International'} };
  const s = map[type] || { bg:'#f3f4f6', color:'#374151', label:type };
  return <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:4, fontSize:'0.78rem', fontWeight:700, whiteSpace:'nowrap', background:s.bg, color:s.color }}>{s.label}</span>;
};

/* ─── highlightMatch ── */
function highlightMatch(text, query) {
  if (!query||!text) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx===-1) return text;
  return <>{text.slice(0,idx)}<mark style={{ background:'#fef08a',color:'#111827',borderRadius:2,padding:'0 1px' }}>{text.slice(idx,idx+query.length)}</mark>{text.slice(idx+query.length)}</>;
}

/* ─── DropdownPortal ── */
const DropdownPortal = ({ anchorRef, children }) => {
  const [pos, setPos] = useState(null);
  const reposition = () => { if(!anchorRef.current) return; const r=anchorRef.current.getBoundingClientRect(); setPos({top:r.bottom,left:r.left,width:r.width}); };
  useEffect(() => { reposition(); window.addEventListener('scroll',reposition,true); window.addEventListener('resize',reposition); return()=>{ window.removeEventListener('scroll',reposition,true); window.removeEventListener('resize',reposition); }; },[anchorRef]); // eslint-disable-line
  if (!pos) return null;
  return ReactDOM.createPortal(
    <div style={{ position:'fixed',top:pos.top,left:pos.left,width:pos.width,zIndex:999999 }}>{children}</div>,
    document.body
  );
};

/* ─── ConsignmentSearchBox ── (all inline styles kept) */
const ConsignmentSearchBox = ({ onSelect, disabled }) => {
  const [query,setQuery]           = useState('');
  const [results,setResults]       = useState([]);
  const [open,setOpen]             = useState(false);
  const [searching,setSearching]   = useState(false);
  const [highlighted,setHL]        = useState(0);
  const [searchError,setSearchError]=useState('');
  const inputRef=useRef(null),wrapRef=useRef(null),listRef=useRef(null);
  const debouncedQuery = useDebounce(query, 300);

  useEffect(()=>{
    if(!debouncedQuery.trim()){setResults([]);setOpen(false);setSearchError('');return;}
    let cancelled=false;
    (async()=>{
      setSearching(true);setSearchError('');
      try{
        const res=await apiClient.get(`/ConsignmentSearch.php?search=${encodeURIComponent(debouncedQuery)}`);
        if(cancelled) return;
        if(res.data.success&&res.data.data?.length>0){setResults(res.data.data);setHL(0);setOpen(true);}
        else{setResults([]);setOpen(true);}
      }catch{if(!cancelled){setSearchError('Search failed — check connection');setOpen(false);}}
      finally{if(!cancelled)setSearching(false);}
    })();
    return()=>{cancelled=true;};
  },[debouncedQuery]);

  useEffect(()=>{const h=(e)=>{if(!wrapRef.current?.contains(e.target)&&!listRef.current?.contains(e.target))setOpen(false);}; document.addEventListener('mousedown',h); return()=>document.removeEventListener('mousedown',h);},[]);
  useEffect(()=>{if(highlighted>=0&&listRef.current)listRef.current.querySelectorAll('[data-item]')[highlighted]?.scrollIntoView({block:'nearest'});},[highlighted]);

  const selectItem=(item)=>{setQuery(item.ConsignmentNo);setOpen(false);setResults([]);onSelect(item);};
  const handleClear=()=>{setQuery('');setResults([]);setOpen(false);setSearchError('');inputRef.current?.focus();};
  const handleKeyDown=(e)=>{
    if(!open||results.length===0) return;
    if(e.key==='ArrowDown'){e.preventDefault();setHL(h=>Math.min(h+1,results.length-1));}
    if(e.key==='ArrowUp')  {e.preventDefault();setHL(h=>Math.max(h-1,0));}
    if(e.key==='Enter')    {e.preventDefault();if(results[highlighted])selectItem(results[highlighted]);}
    if(e.key==='Escape')   {setOpen(false);}
  };

  return (
    <div ref={wrapRef} style={{ position:'relative',width:'100%' }}>
      <div style={{ position:'relative' }}>
        <div style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',pointerEvents:'none',display:'flex',alignItems:'center',color:searching?'#3b82f6':'#9ca3af' }}>
          {searching?<div style={{ width:16,height:16,border:'2px solid #3b82f6',borderTopColor:'transparent',borderRadius:'50%',animation:'pod-csb-spin 0.7s linear infinite' }}/>:<Search size={16}/>}
        </div>
        <input ref={inputRef} type="text" value={query} onChange={e=>setQuery(e.target.value)} onKeyDown={handleKeyDown}
          onFocus={()=>{if(results.length>0)setOpen(true);}}
          placeholder="Type consignment no., shipper or consignee…"
          disabled={disabled} autoFocus
          style={{ width:'100%',padding:'11px 36px 11px 38px',border:open?'2px solid #3b82f6':'1.5px solid #d1d5db',borderRadius:8,fontSize:'0.92rem',background:'#fff',boxSizing:'border-box',outline:'none',boxShadow:open?'0 0 0 3px rgba(59,130,246,0.15)':'none',transition:'border 0.15s, box-shadow 0.15s' }}/>
        {query&&<button type="button" onClick={handleClear} style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#9ca3af',display:'flex',padding:4 }}><X size={15}/></button>}
      </div>

      {open&&(
        <DropdownPortal anchorRef={wrapRef}>
          <div ref={listRef} style={{ background:'#fff',border:'1.5px solid #3b82f6',borderRadius:10,boxShadow:'0 16px 40px rgba(0,0,0,0.18)',maxHeight:360,overflowY:'auto' }}>
            {results.length===0?(
              <div style={{ padding:24,textAlign:'center',color:'#6b7280' }}>
                <Package size={32} style={{ opacity:0.25,display:'block',margin:'0 auto 10px' }}/>
                <div style={{ fontSize:'0.88rem',fontWeight:600 }}>No consignments found</div>
                <div style={{ fontSize:'0.8rem',marginTop:4,color:'#9ca3af' }}>No results for "{debouncedQuery}"</div>
              </div>
            ):(
              <>
                <div style={{ padding:'7px 14px',background:'#f8fafc',borderBottom:'1px solid #e5e7eb',fontSize:'0.75rem',color:'#6b7280',fontWeight:700,letterSpacing:'0.06em',display:'flex',justifyContent:'space-between',alignItems:'center',borderRadius:'10px 10px 0 0' }}>
                  <span>{results.length} RESULT{results.length!==1?'S':''}</span>
                  <span style={{ fontWeight:400,fontSize:'0.72rem' }}>↑↓ navigate · Enter select · Esc close</span>
                </div>
                {results.map((item,idx)=>{
                  const isHL=idx===highlighted;
                  const route=[item.OriginCity||item.OriginCountry,item.DestinationCity||item.DestinationCountry].filter(Boolean).join(' → ');
                  return (
                    <div key={item.ConsignmentID} data-item onMouseEnter={()=>setHL(idx)} onClick={()=>selectItem(item)}
                      style={{ padding:'11px 16px',cursor:'pointer',borderBottom:'1px solid #f1f5f9',background:isHL?'#eff6ff':'#fff',display:'grid',gridTemplateColumns:'1fr auto',gap:12,alignItems:'center',transition:'background 0.08s' }}>
                      <div>
                        <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap' }}>
                          <TypeBadge type={item.Type}/>
                          <span style={{ fontFamily:'monospace',fontWeight:700,fontSize:'0.9rem',color:isHL?'#1d4ed8':'#111827' }}>{highlightMatch(item.ConsignmentNo,query)}</span>
                          {item.ConsignmentDate&&<span style={{ fontSize:'0.76rem',color:'#9ca3af' }}>{new Date(item.ConsignmentDate).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</span>}
                        </div>
                        <div style={{ display:'flex',gap:14,fontSize:'0.81rem',color:'#4b5563',flexWrap:'wrap' }}>
                          {(item.ShipperName||item.Shipper)&&<span style={{ display:'flex',alignItems:'center',gap:3 }}><User size={11} style={{ opacity:0.5 }}/>{highlightMatch(item.ShipperName||item.Shipper,query)}</span>}
                          {(item.ConsigneeName||item.Consignee)&&<span style={{ color:'#6b7280' }}>→ {highlightMatch(item.ConsigneeName||item.Consignee,query)}</span>}
                          {route&&<span style={{ display:'flex',alignItems:'center',gap:3,color:'#9ca3af' }}><MapPin size={11} style={{ opacity:0.5 }}/>{route}</span>}
                        </div>
                      </div>
                      <div style={{ textAlign:'right',display:'flex',flexDirection:'column',alignItems:'flex-end',gap:5 }}>
                        {item.Status&&<span style={{ background:'#dbeafe',color:'#1e40af',padding:'2px 9px',borderRadius:12,fontSize:'0.74rem',fontWeight:600,whiteSpace:'nowrap' }}>{item.Status}</span>}
                        {(item.Weight||item.Pieces)&&<span style={{ fontSize:'0.76rem',color:'#9ca3af',whiteSpace:'nowrap' }}>{item.Weight?`${item.Weight} kg`:''}{item.Pieces?` · ${item.Pieces} pcs`:''}</span>}
                        {isHL&&<span style={{ fontSize:'0.68rem',color:'#3b82f6',fontWeight:700 }}>Enter ↵</span>}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </DropdownPortal>
      )}

      {searchError&&<div style={{ marginTop:6,fontSize:'0.82rem',color:'#ef4444',display:'flex',alignItems:'center',gap:4 }}><AlertCircle size={13}/>{searchError}</div>}
      <style>{`@keyframes pod-csb-spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   PODUpdateForm
══════════════════════════════════════════════════════════════════════════════ */
const PODUpdateForm = () => {
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState('');
  const [success,          setSuccess]          = useState('');
  const [consignmentData,  setConsignmentData]  = useState(null);
  const [courierStatuses,  setCourierStatuses]  = useState([]);
  const [employees,        setEmployees]        = useState([]);
  const [paymentSummary,   setPaymentSummary]   = useState(null);
  const [summaryLoading,   setSummaryLoading]   = useState(false);
  const [showHelp,         setShowHelp]         = useState(false);

  const [podData, setPodData] = useState({
    DeliveryDate:'', DeliveredByID:'', DeliveredBy:'',
    ReceivedBy:'', CourierStatusID:'', PODRemarks:'', OtherChargeDescription:'',
  });

  useEffect(()=>{
    apiClient.get('/CourierStatus.php').then(r=>{if(r.data?.success)setCourierStatuses(r.data.data||[]);}).catch(()=>{});
    apiClient.get('/Employees.php',{params:{isactive:1}}).then(r=>{if(r.data?.success)setEmployees(r.data.data||[]);}).catch(()=>{});
  },[]);

  useEffect(()=>{
    const onKey=(e)=>{ if(e.key==='F5'){e.preventDefault();if(consignmentData)handleSave();} };
    window.addEventListener('keydown',onKey);
    return()=>window.removeEventListener('keydown',onKey);
  },[consignmentData,podData]); // eslint-disable-line

  const handleSelect = (c) => {
    setConsignmentData(c);
    setPodData({ DeliveryDate:c.DeliveryDate||new Date().toISOString().slice(0,10), DeliveredByID:c.DeliveredByID||'', DeliveredBy:c.DeliveredBy||'', ReceivedBy:c.ReceivedBy||'', CourierStatusID:c.CourierStatusID||'', PODRemarks:c.PODRemarks||'', OtherChargeDescription:c.OtherChargeDescription||'' });
    setError(''); setSuccess('');
    setPaymentSummary(null); setSummaryLoading(true);
    apiClient.get(`/ConsignmentPayments.php?consignmentid=${c.ConsignmentID}`)
      .then(r=>{
        if(r.data?.success){
          const rows=r.data.data||[];
          const totalRevenue=rows.reduce((s,p)=>s+(parseFloat(p.TotalAmt)||0),0);
          const totalCost   =rows.reduce((s,p)=>s+(parseFloat(p.CostAmount)||0),0);
          setPaymentSummary({rows,totalRevenue,totalCost,profitLoss:totalRevenue-totalCost});
        }
      }).catch(()=>{}).finally(()=>setSummaryLoading(false));
  };

  const handlePODChange=(e)=>{const{name,value}=e.target;setPodData(prev=>({...prev,[name]:value}));};

  const handleSave=async()=>{
    if(!consignmentData)   {setError('No consignment loaded');return;}
    if(!podData.DeliveryDate){setError('Delivery date is required');return;}
    setLoading(true);setError('');setSuccess('');
    try{
      const res=await apiClient.post('/ConsignmentPOD.php',{ ConsignmentID:consignmentData.ConsignmentID, DeliveryDate:podData.DeliveryDate, DeliveredByID:podData.DeliveredByID||null, DeliveredBy:podData.DeliveredBy||null, ReceivedBy:podData.ReceivedBy||null, CourierStatusID:podData.CourierStatusID||null, PODRemarks:podData.PODRemarks||null, OtherChargeDescription:podData.OtherChargeDescription||null });
      if(res.data.success){setSuccess('POD updated successfully!');setTimeout(()=>setSuccess(''),3000);handleClear();}
      else setError(res.data.message||'Failed to save');
    }catch(err){setError('Error saving POD: '+(err.response?.data?.message||err.message));}
    finally{setLoading(false);}
  };

  const handleClear=()=>{
    setConsignmentData(null);
    setPodData({ DeliveryDate:new Date().toISOString().slice(0,10), DeliveredByID:'', DeliveredBy:'', ReceivedBy:'', CourierStatusID:'', PODRemarks:'', OtherChargeDescription:'' });
    setError('');setSuccess('');setPaymentSummary(null);
  };

  const inpCls='w-full px-3 py-2.5 border border-gray-300 rounded-md text-[0.9rem] bg-white box-border font-[inherit] text-gray-900 outline-none transition-all duration-150 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.15)]';
  const selCls=inpCls+' cursor-pointer';
  const lblCls='block text-[0.76rem] font-bold text-gray-700 uppercase tracking-wide mb-1';
  const SecHdr=({icon:Icon,color,children})=>(
    <div className="flex items-center gap-2 text-[0.95rem] font-semibold text-gray-900 mb-3.5 pb-3 border-b-2 border-slate-100">
      {Icon&&<Icon size={17} style={{ color:color||'#3b82f6' }}/>}{children}
    </div>
  );

  return (
    <div className="px-6 pt-5 pb-16 box-border">

      {/* Toolbar */}
      <div className="flex gap-2.5 flex-wrap mb-5">
        <button type="button" onClick={handleSave} disabled={loading||!consignmentData}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white border-0 rounded-lg text-[0.9rem] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 transition-all duration-150">
          <Save size={15}/> {loading?'Saving…':'Save POD (F5)'}
        </button>
        <PODPrintModal consignmentData={consignmentData} podData={podData} paymentSummary={paymentSummary} companyName={companyName}/>
        <button type="button" onClick={handleClear} disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white border-0 rounded-lg text-[0.9rem] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 transition-all duration-150">
          <RotateCcw size={15}/> Clear
        </button>
        <button type="button" onClick={() => setShowHelp(true)} title="How to use this page"
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white border-0 rounded-lg text-[0.9rem] font-semibold cursor-pointer hover:brightness-110 transition-all duration-150">
          <HelpCircle size={15}/> Help
        </button>
      </div>

      <div className="max-w-[1080px] mx-auto">

        {/* Alerts */}
        {error  &&<div className="flex items-center gap-2 px-4 py-3 bg-red-100 border border-red-300 text-red-700 rounded-lg mb-3.5 text-[0.9rem]"><AlertCircle size={17}/><strong>Error:</strong> {error}</div>}
        {success&&<div className="flex items-center gap-2 px-4 py-3 bg-emerald-100 border border-emerald-300 text-emerald-800 rounded-lg mb-3.5 text-[0.9rem]"><CheckCircle size={17}/><strong>Success:</strong> {success}</div>}

        {/* Search card */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
          <SecHdr icon={Search}>Search Consignment</SecHdr>
          {consignmentData ? (
            <div className="flex items-center gap-2.5 px-3.5 py-2.5 bg-blue-50 border-[1.5px] border-blue-200 rounded-lg flex-wrap">
              <Package size={15} className="flex-shrink-0 text-blue-600"/>
              <span className="font-mono font-bold text-[0.92rem] text-blue-700">{consignmentData.ConsignmentNo}</span>
              <span className="text-[0.82rem] text-gray-700 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                {consignmentData.ShipperName||consignmentData.Shipper||''}
                {(consignmentData.ConsigneeName||consignmentData.Consignee)?` → ${consignmentData.ConsigneeName||consignmentData.Consignee}`:''}
              </span>
              <button type="button" onClick={handleClear}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-300 rounded-md text-[0.8rem] font-semibold text-gray-700 cursor-pointer flex-shrink-0 hover:bg-gray-100 transition-colors duration-100">
                <X size={13}/> Change
              </button>
            </div>
          ) : (
            <>
              <ConsignmentSearchBox onSelect={handleSelect} disabled={loading}/>
              <p className="mt-2 text-[0.77rem] text-gray-400 m-0">
                Type consignment number, shipper, or consignee — results appear instantly. Use ↑↓ &amp; Enter to select.
              </p>
            </>
          )}
        </div>

        {/* Consignment details */}
        {consignmentData && (
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-5 mb-4">
            <div className="flex items-center gap-2 text-[0.95rem] font-semibold text-sky-700 mb-3.5 pb-3 border-b-2 border-sky-200">
              <Package size={17}/> Consignment Details
            </div>
            <div className="grid gap-3.5" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))' }}>
              {[
                ['Consignment No', <span className="font-mono font-semibold text-sky-900 text-[0.87rem]">{consignmentData.ConsignmentNo}</span>],
                ['Date', <span className="font-semibold text-sky-900 text-[0.87rem]">{new Date(consignmentData.ConsignmentDate).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</span>],
                ['Shipper', consignmentData.ShipperName||consignmentData.Shipper||'-'],
                ['Consignee', consignmentData.ConsigneeName||consignmentData.Consignee||'-'],
                ['Origin', consignmentData.OriginCity||consignmentData.OriginCountry||'N/A'],
                ['Destination', consignmentData.DestinationCity||consignmentData.DestinationCountry||'N/A'],
                ['Pieces', consignmentData.Pieces||0],
                ['Weight', `${consignmentData.Weight||consignmentData.StatedWeight||0} kg`],
                ['Type', <TypeBadge type={consignmentData.Type||consignmentData.ConsignmentType}/>],
              ].map(([label,val])=>(
                <div key={label}>
                  <label className="block text-[0.69rem] font-bold text-sky-600 uppercase tracking-wide mb-0.5">{label}:</label>
                  <div className="font-semibold text-sky-900 text-[0.87rem]">{val}</div>
                </div>
              ))}
              {consignmentData.Description&&(
                <div className="col-span-full">
                  <label className="block text-[0.69rem] font-bold text-sky-600 uppercase tracking-wide mb-0.5">Description:</label>
                  <div className="font-semibold text-sky-900 text-[0.87rem]">{consignmentData.Description}</div>
                </div>
              )}
            </div>

            {/* Payment reference */}
            <div className="mt-4 bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-[0.82rem] font-semibold text-gray-700">
                <TrendingUp size={14}/> Payment Reference
              </div>
              {summaryLoading?(
                <div className="px-4 py-3 text-sm text-gray-400">Loading payment data…</div>
              ):paymentSummary&&paymentSummary.rows.length>0?(
                <>
                  <div className="grid grid-cols-4 gap-px bg-gray-200 border-b border-gray-200">
                    {[['Entries',paymentSummary.rows.length,'text-gray-800'],['Revenue',paymentSummary.totalRevenue.toFixed(2),'text-emerald-700'],['Cost',paymentSummary.totalCost.toFixed(2),'text-gray-800'],['P / L',(paymentSummary.profitLoss>=0?'+':'')+paymentSummary.profitLoss.toFixed(2),paymentSummary.profitLoss>=0?'text-emerald-700':'text-red-600']].map(([l,v,cls])=>(
                      <div key={l} className="bg-gray-50 px-3 py-2 text-center">
                        <div className="text-[0.62rem] font-bold uppercase tracking-wide text-gray-400 mb-0.5">{l}</div>
                        <div className={`text-[0.95rem] font-bold font-mono ${cls}`}>{v}</div>
                      </div>
                    ))}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-[0.75rem]">
                      <thead><tr className="bg-gray-50 border-b border-gray-200">
                        {['Payment Mode','Customer','Item','Revenue','Tax','Total','Cost','P / L'].map((h,i)=>(
                          <th key={h} className={`px-2 py-1.5 text-left font-bold text-[0.6rem] uppercase tracking-wide text-gray-500 border border-gray-200 ${i>=3?'text-right':''}`}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {paymentSummary.rows.map((p,i)=>{
                          const pl=parseFloat(p.TotalAmt||0)-parseFloat(p.CostAmount||0);
                          return <tr key={i} className="border-b border-gray-100">
                            <td className="px-2 py-1.5 border border-gray-100">{p.PaymentMode||'-'}</td>
                            <td className="px-2 py-1.5 border border-gray-100">{p.CustomerName||'-'}</td>
                            <td className="px-2 py-1.5 border border-gray-100">{p.revenuetypename||'-'}</td>
                            <td className="px-2 py-1.5 text-right font-mono border border-gray-100">{parseFloat(p.Amount||0).toFixed(2)}</td>
                            <td className="px-2 py-1.5 text-right font-mono border border-gray-100">{parseFloat(p.TaxAmt||0).toFixed(2)}</td>
                            <td className="px-2 py-1.5 text-right font-mono font-bold border border-gray-100">{parseFloat(p.TotalAmt||0).toFixed(2)}</td>
                            <td className="px-2 py-1.5 text-right font-mono border border-gray-100">{parseFloat(p.CostAmount||0).toFixed(2)}</td>
                            <td className={`px-2 py-1.5 text-right font-mono font-bold border border-gray-100 ${pl>=0?'text-emerald-700':'text-red-600'}`}>{pl.toFixed(2)}</td>
                          </tr>;
                        })}
                      </tbody>
                      <tfoot><tr className="bg-gray-50 border-t-2 border-gray-200">
                        <td colSpan={5} className="px-2 py-2 text-right font-bold border border-gray-200">Totals</td>
                        <td className="px-2 py-2 text-right font-mono font-bold border border-gray-200">{paymentSummary.totalRevenue.toFixed(2)}</td>
                        <td className="px-2 py-2 text-right font-mono font-bold border border-gray-200">{paymentSummary.totalCost.toFixed(2)}</td>
                        <td className={`px-2 py-2 text-right font-mono font-bold border border-gray-200 ${paymentSummary.profitLoss>=0?'text-emerald-700':'text-red-600'}`}>{paymentSummary.profitLoss>=0?'+':''}{paymentSummary.profitLoss.toFixed(2)}</td>
                      </tr></tfoot>
                    </table>
                  </div>
                </>
              ):(
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-400">
                  <DollarSign size={18} className="opacity-30"/> No payment entries found for this consignment
                </div>
              )}
            </div>
          </div>
        )}

        {consignmentData&&(<>
          {/* Section 1: Delivery Date & Status */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
            <SecHdr icon={Calendar}>1. Delivery Date &amp; Status</SecHdr>
            <div className="grid gap-4" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(210px,1fr))' }}>
              <div><label className={lblCls}>Delivery Date *</label><input type="date" name="DeliveryDate" value={podData.DeliveryDate} onChange={handlePODChange} required className={inpCls}/></div>
              <div><label className={lblCls}>Courier Status</label>
                <select name="CourierStatusID" value={podData.CourierStatusID} onChange={handlePODChange} className={selCls}>
                  <option value="">-- Select Status --</option>
                  {courierStatuses.map(s=><option key={s.CourierStatusID} value={s.CourierStatusID}>{s.StatusName}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Section 2: Delivery Confirmation */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
            <SecHdr icon={CheckCircle}>2. Delivery Confirmation</SecHdr>
            <div className="grid gap-4" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(210px,1fr))' }}>
              <div>
                <label className={lblCls}>Delivered By (Employee)</label>
                <select name="DeliveredByID" value={podData.DeliveredByID} onChange={handlePODChange} className={selCls}>
                  <option value="">-- Select Employee --</option>
                  {employees.length>0?employees.map(emp=><option key={emp.employeeid} value={emp.employeeid}>{emp.employeename}{emp.employeecode?` (${emp.employeecode})`:''}</option>):<option value="" disabled>No employees found</option>}
                </select>
                {employees.length===0&&<small className="mt-1 text-[12px] text-red-500 block">No active employees. Check Employees master.</small>}
              </div>
              <div><label className={lblCls}>Delivered By (Name)</label><input type="text" name="DeliveredBy" value={podData.DeliveredBy} onChange={handlePODChange} placeholder="Person who delivered" className={inpCls}/></div>
              <div><label className={lblCls}>Received By</label><input type="text" name="ReceivedBy" value={podData.ReceivedBy} onChange={handlePODChange} placeholder="Person who received" className={inpCls}/></div>
            </div>
          </div>

          {/* Section 3: Remarks */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
            <SecHdr icon={FileText}>3. Remarks</SecHdr>
            <div className="grid gap-4" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(210px,1fr))' }}>
              <div className="col-span-full"><label className={lblCls}>POD Remarks</label><textarea name="PODRemarks" value={podData.PODRemarks} onChange={handlePODChange} rows={3} placeholder="Any remarks regarding delivery…" className={`${inpCls} resize-y min-h-[72px]`}/></div>
              <div className="col-span-full"><label className={lblCls}>Other Charge Description</label><input type="text" name="OtherChargeDescription" value={podData.OtherChargeDescription} onChange={handlePODChange} placeholder="Additional charges description" className={inpCls}/></div>
            </div>
          </div>

          {/* Bottom save */}
          <div className="flex justify-end pt-2">
            <button type="button" onClick={handleSave} disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white border-0 rounded-lg text-[0.9rem] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 transition-all duration-150">
              <Save size={15}/> {loading?'Saving…':'Save POD'}
            </button>
          </div>
        </>)}

      </div>

      {showHelp && (
        <div className="fixed inset-0 flex items-center justify-center p-5" style={{ background: 'rgba(0,0,0,0.65)', zIndex: 999999 }}
          onClick={() => setShowHelp(false)}>
          <div className="bg-white rounded-xl w-full max-w-[640px] max-h-[85vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 rounded-t-xl flex-shrink-0" style={{ background: '#3b82f6' }}>
              <h3 className="flex items-center gap-2 text-lg font-semibold m-0 text-white">
                <HelpCircle size={18} /> How to Use This Page
              </h3>
              <button onClick={() => setShowHelp(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg border-0 cursor-pointer bg-white/20 hover:bg-white/30 transition-colors duration-150 text-white">
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {[
                { title: 'Find a Consignment', desc: 'Type a consignment number, shipper, or consignee name in the search box. Use ↑↓ to navigate results and Enter to select.' },
                { title: 'Review Details & Payments', desc: 'Once selected, consignment details and any existing payment entries (revenue, cost, profit/loss) are shown for reference.' },
                { title: 'Delivery Date & Status', desc: 'Set the Delivery Date (required) and update the Courier Status to reflect the delivery outcome.' },
                { title: 'Delivery Confirmation', desc: 'Record who delivered it (from Employees, or free text) and who received it at the destination.' },
                { title: 'Remarks', desc: 'Add any POD remarks or a description for additional charges, if applicable.' },
                { title: 'Save', desc: 'Click "Save POD" (or press F5) to record the proof of delivery. This clears the form for the next consignment.' },
              ].map(({ title, desc }, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: '#3b82f6' }}>
                    {i + 1}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-800 mb-0.5">{title}</div>
                    <div className="text-sm text-gray-600">{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end px-6 py-4 border-t border-gray-200 flex-shrink-0">
              <button onClick={() => setShowHelp(false)}
                className="px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer" style={{ background: '#3b82f6' }}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PODUpdateForm;